import React, { useEffect, useRef, useState } from 'react';
import { callApi } from '../api/api';

// ── 4-Digit MPIN Box Component ────────────────────────────────────────────────
// Uses a single hidden input (captures all native events including mobile keyboard)
// with 4 visual boxes displayed on top.
const MpinInput = ({ value, onChange, id }) => {
    const hiddenRef = useRef();
    const digits = (value + '    ').slice(0, 4).split('');

    const handleChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
        onChange(raw);
    };

    const focusHidden = () => hiddenRef.current && hiddenRef.current.focus();

    return (
        <div style={{ position: 'relative', display: 'flex', gap: '12px', justifyContent: 'center' }} onClick={focusHidden}>
            {/* Hidden real input that captures all input */}
            <input
                ref={hiddenRef}
                id={id}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={value}
                onChange={handleChange}
                autoComplete="one-time-code"
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    cursor: 'pointer',
                    zIndex: 2,
                    fontSize: '16px' // prevents iOS zoom
                }}
            />
            {/* Visual digit boxes */}
            {[0, 1, 2, 3].map(i => {
                const filled = digits[i] && digits[i] !== ' ';
                const isActive = value.length === i;
                return (
                    <div
                        key={i}
                        style={{
                            width: '54px',
                            height: '60px',
                            fontSize: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: isActive ? '2px solid #059669' : filled ? '2px solid #059669' : '2px solid #d1d5db',
                            borderRadius: '12px',
                            background: '#f9fafb',
                            boxShadow: isActive ? '0 0 0 3px rgba(5,150,105,0.15)' : 'none',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                            color: '#1f2937',
                            fontWeight: 700,
                            pointerEvents: 'none',
                            userSelect: 'none',
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        {filled ? '●' : isActive ? <span style={{ width: '2px', height: '28px', background: '#059669', animation: 'blink 1s step-end infinite', display: 'inline-block' }} /> : ''}
                    </div>
                );
            })}
            <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
        </div>
    );
};

// ── Main LoginRegisterView ────────────────────────────────────────────────────
const LoginRegisterView = ({
    handleSendOtp,
    handleVerifyOtp,
    handleCompleteRegistration,
    handlePasswordLogin,
    handleResetPassword,
    otpStep,
    registrationStep,
    forgotPasswordStep,
    setForgotPasswordStep,
    pendingIdToken,
    authError,
    clearAuthError
}) => {
    // 'phone'      - entry step for checking if user exists
    // 'mpin'       – mobile + MPIN direct login
    // 'otp'        – OTP phone entry (for new-user registration)
    const [loginMethod, setLoginMethod] = useState('phone');

    const [mpin, setMpin] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    // Registration form fields
    const [regName, setRegName] = useState('');
    const [regGender, setRegGender] = useState('Male');
    const [regAge, setRegAge] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regAddress, setRegAddress] = useState('');
    const [regMpin, setRegMpin] = useState('');
    const [regConfirmMpin, setRegConfirmMpin] = useState('');
    const [regError, setRegError] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [mpinShake, setMpinShake] = useState(false);

    useEffect(() => {
        clearAuthError();
        setRegError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpStep, registrationStep]);

    useEffect(() => {
        let interval;
        if (otpStep && resendTimer > 0) {
            interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [otpStep, resendTimer]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const fmt = (num) => {
        const n = num.trim();
        return n.startsWith('+') ? n : `+91${n}`;
    };

    const onMpinLogin = async (e) => {
        e.preventDefault();
        try { if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission(); } catch (e) {}
        if (mpin.length !== 4) return;
        setIsSubmitting(true);
        const loginError = await handlePasswordLogin(fmt(phoneNumber), mpin);
        setIsSubmitting(false);
        // Only clear MPIN and shake on failure (when an error is returned)
        if (loginError) {
            setMpin('');
            setMpinShake(true);
            setTimeout(() => setMpinShake(false), 600);
        }
    };

    const onCheckUser = async (e) => {
        e.preventDefault();
        const cleanPhone = phoneNumber.trim();
        if (cleanPhone.length < 10) return;
        
        setIsSubmitting(true);
        try {
            const res = await callApi('/users/check-user', 'POST', { mobile: cleanPhone });
            if (res.exists) {
                setLoginMethod('mpin');
            } else {
                setLoginMethod('otp');
            }
        } catch (err) {
            console.error("Check user error", err);
            // Fallback to OTP on error
            setLoginMethod('otp');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onSendOtp = async (e) => {
        if (e) e.preventDefault();
        try { if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission(); } catch (e) {}
        setIsSubmitting(true);
        await handleSendOtp(fmt(phoneNumber));
        setResendTimer(30);
        setIsSubmitting(false);
    };

    const onVerifyOtp = async (e) => {
        e.preventDefault();
        try { if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission(); } catch (e) {}
        setIsSubmitting(true);
        await handleVerifyOtp(otpCode);
        setIsSubmitting(false);
    };

    const onCompleteRegistration = (e) => {
        e.preventDefault();
        setRegError('');
        if (!regName || !regName.trim()) { setRegError('Please enter your full name.'); return; }
        if (!regGender) { setRegError('Please select your gender.'); return; }
        if (!regAge || parseInt(regAge) <= 0 || parseInt(regAge) > 120) { setRegError('Please enter a valid age (1-120).'); return; }
        if (!regEmail || !regEmail.trim()) { setRegError('Please enter your email address.'); return; }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(regEmail.trim())) { setRegError('Please enter a valid email address.'); return; }
        if (!regAddress || !regAddress.trim()) { setRegError('Please enter your address.'); return; }
        if (regMpin.length !== 4) { setRegError('MPIN must be exactly 4 digits.'); return; }
        if (regMpin !== regConfirmMpin) { setRegError('MPINs do not match.'); return; }
        setIsSubmitting(true);
        handleCompleteRegistration({
            name: regName.trim(),
            gender: regGender,
            age: parseInt(regAge),
            email: regEmail.trim(),
            address: regAddress.trim(),
            mpin: regMpin
        }).finally(() => setIsSubmitting(false));
    };

    // ── Styles ────────────────────────────────────────────────────────────────

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        border: '1.5px solid #d1d5db',
        borderRadius: '10px',
        fontSize: '16px',
        outline: 'none',
        background: '#f9fafb',
        boxSizing: 'border-box'
    };

    const primaryBtn = (disabled) => ({
        width: '100%',
        padding: '14px',
        background: disabled ? '#9ca3af' : '#059669',
        color: '#fff',
        fontWeight: 700,
        fontSize: '17px',
        border: 'none',
        borderRadius: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        marginTop: '8px',
        transition: 'background 0.2s'
    });

    const linkBtn = {
        background: 'none',
        border: 'none',
        color: '#0284c7',
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: '14px',
        padding: 0
    };

    const card = {
        maxWidth: '420px',
        margin: '0 auto',
        padding: '32px 28px',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.10)'
    };

    const divider = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        margin: '16px 0',
        color: '#9ca3af',
        fontSize: '13px'
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <section id="login" style={card}>
            <h2 style={{ textAlign: 'center', fontSize: '26px', fontWeight: 800, color: '#0369a1', marginBottom: '24px' }}>
                Login to QwickMed
            </h2>

            {/* ── PHONE CHECK ENTRY ── */}
            {!otpStep && !registrationStep && loginMethod === 'phone' && (
                <form id="phone-check-form" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onSubmit={onCheckUser}>
                    <div>
                        <label htmlFor="check-mobile" style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
                            Mobile Number
                        </label>
                        <input
                            type="tel"
                            id="check-mobile"
                            style={inputStyle}
                            placeholder="9876543210"
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value)}
                            autoComplete="tel"
                            required
                        />
                    </div>
                    <button type="submit" disabled={isSubmitting || phoneNumber.length < 10} style={primaryBtn(isSubmitting || phoneNumber.length < 10)}>
                        {isSubmitting ? 'Checking...' : 'Continue'}
                    </button>
                </form>
            )}

            {/* ── MPIN LOGIN (default, no OTP needed) ── */}
            {!otpStep && !registrationStep && loginMethod === 'mpin' && (
                <form id="mpin-login-form" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onSubmit={onMpinLogin}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <label htmlFor="login-mobile-mpin" style={{ display: 'block', fontWeight: 600, color: '#374151' }}>
                                Mobile Number
                            </label>
                            <button type="button" style={linkBtn} onClick={() => setLoginMethod('phone')}>Change</button>
                        </div>
                        <input
                            type="tel"
                            id="login-mobile-mpin"
                            style={inputStyle}
                            placeholder="9876543210"
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value)}
                            autoComplete="tel"
                            disabled
                            required
                        />
                    </div>

                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label style={{ fontWeight: 600, color: '#374151' }}>MPIN</label>
                            <button
                                type="button"
                                style={linkBtn}
                                onClick={() => { setForgotPasswordStep(true); setLoginMethod('otp'); clearAuthError(); }}
                            >
                                Forgot MPIN?
                            </button>
                        </div>
                        <MpinInput id="login-mpin" value={mpin} onChange={setMpin} />
                        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>Enter your 4-digit MPIN</p>
                    </div>

                    <button type="submit" disabled={isSubmitting || mpin.length !== 4} style={primaryBtn(isSubmitting || mpin.length !== 4)}>
                        {isSubmitting ? 'Logging in...' : 'Login'}
                    </button>

                    {/* Divider + Register link */}
                    <div style={divider}>
                        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                        Or Login with OTP
                        <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                    </div>
                    <button
                        type="button"
                        style={{ ...primaryBtn(false), background: '#0284c7', marginTop: 0 }}
                        onClick={() => { setLoginMethod('otp'); clearAuthError(); setMpin(''); }}
                    >
                        Login with OTP
                    </button>
                </form>
            )}

            {/* ── OTP PHONE ENTRY (registration / forgot MPIN) ── */}
            {!otpStep && !registrationStep && loginMethod === 'otp' && (
                <form id="phone-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onSubmit={onSendOtp}>
                    <p style={{ textAlign: 'center', fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                        {forgotPasswordStep
                            ? 'Enter your mobile number to reset your MPIN.'
                            : 'This mobile number is not registered yet. We will send an OTP to verify.'}
                    </p>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <label htmlFor="login-mobile" style={{ display: 'block', fontWeight: 600, color: '#374151' }}>
                                Mobile Number
                            </label>
                            <button type="button" style={linkBtn} onClick={() => { setLoginMethod('phone'); setForgotPasswordStep(false); }}>Change</button>
                        </div>
                        <input
                            type="tel"
                            id="login-mobile"
                            style={inputStyle}
                            placeholder="9876543210"
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value)}
                            autoComplete="tel"
                            disabled={!forgotPasswordStep}
                            required
                        />
                    </div>
                    <button type="submit" disabled={isSubmitting} style={primaryBtn(isSubmitting)}>
                        {isSubmitting ? 'Sending...' : 'Send OTP'}
                    </button>

                    {/* Back to MPIN login */}
                    {!forgotPasswordStep && (
                        <div style={{ textAlign: 'center' }}>
                            <button type="button" style={linkBtn} onClick={() => { setLoginMethod('phone'); clearAuthError(); }}>
                                ← Go Back
                            </button>
                        </div>
                    )}
                </form>
            )}

            {/* ── OTP VERIFY ── */}
            {otpStep && !registrationStep && (!forgotPasswordStep || (forgotPasswordStep && !pendingIdToken)) && (
                <form id="otp-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} onSubmit={onVerifyOtp}>
                    <div>
                        <label htmlFor="otp-code" style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
                            Enter OTP
                        </label>
                        <input
                            type="text"
                            id="otp-code"
                            style={{ ...inputStyle, textAlign: 'center', letterSpacing: '10px', fontSize: '22px' }}
                            placeholder="● ● ● ● ● ●"
                            maxLength="6"
                            value={otpCode}
                            onChange={e => setOtpCode(e.target.value)}
                            autoComplete="one-time-code"
                            required
                        />
                    </div>
                    <button type="submit" disabled={isSubmitting} style={primaryBtn(isSubmitting)}>
                        {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                    </button>
                    <div style={{ textAlign: 'center', marginTop: '4px' }}>
                        {resendTimer > 0 ? (
                            <p style={{ color: '#6b7280', fontSize: '14px' }}>Resend OTP in {resendTimer}s</p>
                        ) : (
                            <button type="button" style={linkBtn} onClick={onSendOtp} disabled={isSubmitting}>
                                Resend OTP
                            </button>
                        )}
                    </div>
                </form>
            )}

            {/* ── SET NEW MPIN (Forgot MPIN flow) ── */}
            {forgotPasswordStep && pendingIdToken && (
                <form id="reset-mpin-form" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} onSubmit={async (e) => {
                    e.preventDefault();
                    setRegError('');
                    if (regMpin.length !== 4) { setRegError('MPIN must be exactly 4 digits.'); return; }
                    if (regMpin !== regConfirmMpin) { setRegError('MPINs do not match.'); return; }
                    setIsSubmitting(true);
                    await handleResetPassword(regMpin);
                    setIsSubmitting(false);
                }}>
                    <h3 style={{ textAlign: 'center', fontSize: '20px', fontWeight: 700, color: '#1f2937', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        Set New MPIN
                    </h3>
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', color: '#374151' }}>New MPIN</label>
                        <MpinInput id="reset-mpin" value={regMpin} onChange={setRegMpin} />
                    </div>
                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', color: '#374151' }}>Confirm New MPIN</label>
                        <MpinInput id="reset-confirm-mpin" value={regConfirmMpin} onChange={setRegConfirmMpin} />
                    </div>
                    <button type="submit" disabled={isSubmitting} style={primaryBtn(isSubmitting)}>
                        {isSubmitting ? 'Saving...' : 'Set MPIN'}
                    </button>
                </form>
            )}

            {/* ── COMPLETE REGISTRATION ── */}
            {registrationStep && (
                <form id="registration-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }} onSubmit={onCompleteRegistration}>
                    <h3 style={{ textAlign: 'center', fontSize: '20px', fontWeight: 700, color: '#1f2937', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                        Complete Registration
                    </h3>

                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '5px', color: '#374151' }}>Full Name *</label>
                        <input
                            type="text"
                            style={inputStyle}
                            value={regName}
                            onChange={e => setRegName(e.target.value)}
                            placeholder="Enter your full name"
                            required
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
                            Gender *
                        </label>
                        <div style={{ display: 'flex', gap: '24px', alignItems: 'center', padding: '10px 14px', background: '#f9fafb', borderRadius: '10px', border: '1.5px solid #d1d5db' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                                <input
                                    type="radio"
                                    name="reg-gender"
                                    value="Male"
                                    checked={regGender === 'Male'}
                                    onChange={() => setRegGender('Male')}
                                    style={{ accentColor: '#059669', width: '18px', height: '18px', cursor: 'pointer' }}
                                    required
                                />
                                Male
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                                <input
                                    type="radio"
                                    name="reg-gender"
                                    value="Female"
                                    checked={regGender === 'Female'}
                                    onChange={() => setRegGender('Female')}
                                    style={{ accentColor: '#059669', width: '18px', height: '18px', cursor: 'pointer' }}
                                    required
                                />
                                Female
                            </label>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '5px', color: '#374151' }}>Age *</label>
                            <input
                                type="number"
                                min="1"
                                max="120"
                                style={inputStyle}
                                value={regAge}
                                onChange={e => setRegAge(e.target.value)}
                                placeholder="Age (e.g. 35)"
                                required
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '5px', color: '#374151' }}>Email *</label>
                            <input
                                type="email"
                                style={inputStyle}
                                value={regEmail}
                                onChange={e => setRegEmail(e.target.value)}
                                placeholder="name@example.com"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '5px', color: '#374151' }}>Address *</label>
                        <textarea
                            style={{ ...inputStyle, resize: 'vertical' }}
                            rows={2}
                            value={regAddress}
                            onChange={e => setRegAddress(e.target.value)}
                            placeholder="Full address (flat/house no, street, area, city, pincode)"
                            required
                        />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', color: '#374151' }}>
                            Set MPIN * <span style={{ fontWeight: 400, fontSize: '13px', color: '#6b7280' }}>(4 digits)</span>
                        </label>
                        <MpinInput id="reg-mpin" value={regMpin} onChange={setRegMpin} />
                    </div>

                    <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', color: '#374151' }}>Confirm MPIN *</label>
                        <MpinInput id="reg-confirm-mpin" value={regConfirmMpin} onChange={setRegConfirmMpin} />
                    </div>

                    {(authError || regError) && (
                        <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '8px', textAlign: 'center', fontSize: '14px' }}>
                            {regError || authError}
                        </div>
                    )}

                    <button type="submit" disabled={isSubmitting} style={primaryBtn(isSubmitting)}>
                        {isSubmitting ? 'Registering...' : 'Register & Continue'}
                    </button>
                </form>
            )}

            {/* ── Error ── */}
            {(authError || regError) && (
                <div style={{ marginTop: '16px', padding: '12px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '8px', textAlign: 'center', fontSize: '14px' }}>
                    {regError || authError}
                </div>
            )}
        </section>
    );
};

export default LoginRegisterView;
